"use client";

import { useAuth } from "@clerk/clerk-react";
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { CourseCard } from "@/lib/types/course";
import {
    COURSE_NAV_CACHE_VERSION,
    buildLessonToCourseIdIndex,
    makeCourseNavStorageKey,
    parsePersistedCourseNavigationCache,
} from "@/lib/contexts/course-navigation-cache";
import type { CourseCertificate } from "@/lib/certificate-display";

/** Full CourseDetail shape - matches what CourseDetail component expects */
export type CachedCourseData = {
    id: string;
    uuid?: string | null;
    title: string;
    description: string;
    track: string;
    level: string;
    /** Enrolled start level name; absent until learner enrolls in a level. */
    placementStartLevel?: string | null;
    totalDuration: string;
    lastUpdated: string;
    progress: number;
    placementDetermined: boolean;
    hasPlacementQuiz?: boolean;
    certificates?: CourseCertificate[];
    organizationName?: string | null;
    accessMode?: "SPONSORED" | "INDIVIDUAL" | string | null;
    userDomain?: string | null;
    /** Client-side timestamp for cache freshness of /api/courses/[id]/core payload. */
    coreFetchedAt?: number;
    /** Folded from GET /courses/{id}/user-settings — see F10 in docs/api_performance_audit.md. */
    userSettings?: {
        hasSettings: boolean;
        requireUserFutureGoal?: boolean;
        settings?: {
            domainId?: number | null;
            futureGoalId?: number | null;
            departmentId?: number | null;
            language?: string;
        } | null;
        domains: { id: number; label: string }[];
        futureGoals: { id: number; label: string }[];
        departments?: { id: number; label: string }[];
    } | null;
    /** Folded from GET /courses/{id}/resources counts — `total` plus `module_<id>` for unlocked modules. */
    resourceCounts?: Record<string, number> | null;
    modules: {
        id: number;
        title: string;
        duration: string;
        completed: boolean;
        isLocked: boolean;
        /** Sum of interactive points earned across this module's lessons. */
        earnedPoints?: number;
        hasModuleQuiz?: boolean;
        moduleQuizPassed?: boolean;
        moduleQuizAvailable?: boolean;
        moduleQuizAttempted?: boolean;
        moduleQuizScore?: number | null;
        moduleQuizWrongCount?: number | null;
        moduleQuizPassThreshold?: number | null;
        level: { id: number; name: string; level_number: number } | null;
        lessons: {
            id: string;
            canonicalLessonId?: number;
            title: string;
            duration: string;
            completed: boolean;
            isLocked: boolean;
            /** Interactive points earned in this lesson. */
            earnedPoints?: number;
            isPreparing?: boolean;
            hasContent?: boolean;
            quizId?: number;
            lastRelevantQuizAt?: Date | string | null;
            remedialLessons?: { uuid: string }[];
            type?: string;
        }[];
    }[];
};

type CourseNavigationContextType = {
    prefetchedCourseData: Record<string, CachedCourseData>;
    setPrefetchedCourseData: (courseId: string, data: CachedCourseData | null) => void;
    setBulkPrefetchedCourseData: (dataByCourseId: Record<string, CachedCourseData>) => void;
    getPrefetchedCourse: (courseId: string) => CachedCourseData | null;
    prefetchedStreak: number | null;
    setPrefetchedStreak: (streak: number | null) => void;
    clearCourseNavigationCache: (courseId: string, options?: { clearStreak?: boolean }) => void;
    /** Snapshot from My Courses list for instant header while course detail loads. */
    setCourseListPreview: (courseId: string, data: CourseCard | null) => void;
    getCourseListPreview: (courseId: string) => CourseCard | null;
    getCourseIdForLesson: (lessonId: string) => string | null;
    setCoursesListCache: (cacheKey: string, courses: CourseCard[] | null) => void;
    getCoursesListCache: (cacheKey: string) => CourseCard[] | null;
};

const CourseNavigationContext = createContext<CourseNavigationContextType | null>(null);

export function CourseNavigationProvider({ children }: { children: React.ReactNode }) {
    const { userId } = useAuth();
    const initialStorageKey =
        typeof window !== "undefined" ? makeCourseNavStorageKey(userId) : null;
    const initialPersisted =
        typeof window !== "undefined" && initialStorageKey
            ? parsePersistedCourseNavigationCache(window.sessionStorage.getItem(initialStorageKey))
            : null;
    const lastUserIdRef = useRef<string | null | undefined>(undefined);
    const [prefetchedCourseData, setPrefetchedCourseDataState] = useState<Record<string, CachedCourseData>>(
        () => initialPersisted?.prefetchedCourseData ?? {},
    );
    const [courseListPreviewById, setCourseListPreviewByIdState] = useState<Record<string, CourseCard>>(
        () => initialPersisted?.courseListPreviewById ?? {},
    );
    const [coursesListByKey, setCoursesListByKeyState] = useState<Record<string, CourseCard[]>>(
        () => initialPersisted?.coursesListByKey ?? {},
    );
    const [prefetchedStreak, setPrefetchedStreak] = useState<number | null>(
        () => initialPersisted?.prefetchedStreak ?? null,
    );
    const hydratedUserRef = useRef<string | null>(initialStorageKey);

    const lessonToCourseId = useMemo(
        () => buildLessonToCourseIdIndex(prefetchedCourseData),
        [prefetchedCourseData],
    );

    // Prevent cross-account cache leakage when users sign out/sign in in the same browser session.
    useEffect(() => {
        if (lastUserIdRef.current === undefined) {
            lastUserIdRef.current = userId;
            return;
        }
        if (lastUserIdRef.current !== userId) {
            setPrefetchedCourseDataState({});
            setCourseListPreviewByIdState({});
            setCoursesListByKeyState({});
            setPrefetchedStreak(null);
            hydratedUserRef.current = null;
            lastUserIdRef.current = userId;
            return;
        }
        lastUserIdRef.current = userId;
    }, [userId]);

    // Hydrate per-user navigation cache from sessionStorage.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const storageKey = makeCourseNavStorageKey(userId);
        if (hydratedUserRef.current === storageKey) return;
        const parsed = parsePersistedCourseNavigationCache(window.sessionStorage.getItem(storageKey));
        if (parsed) {
            setPrefetchedCourseDataState(parsed.prefetchedCourseData ?? {});
            setCourseListPreviewByIdState(parsed.courseListPreviewById ?? {});
            setCoursesListByKeyState(parsed.coursesListByKey ?? {});
            setPrefetchedStreak(parsed.prefetchedStreak ?? null);
        }
        hydratedUserRef.current = storageKey;
    }, [userId]);

    // Persist cache to sessionStorage for fast hard-refresh and direct lesson reopen.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const storageKey = makeCourseNavStorageKey(userId);
        const payload = {
            version: COURSE_NAV_CACHE_VERSION,
            prefetchedCourseData,
            courseListPreviewById,
            coursesListByKey,
            prefetchedStreak,
        };
        try {
            window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
        } catch {
            // Ignore quota/security errors; in-memory cache still works.
        }
    }, [userId, prefetchedCourseData, courseListPreviewById, coursesListByKey, prefetchedStreak]);

    const setPrefetchedCourseData = useCallback((courseId: string, data: CachedCourseData | null) => {
        setPrefetchedCourseDataState(prev => {
            if (!data) {
                if (!(courseId in prev)) return prev;
                const { [courseId]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [courseId]: data };
        });
    }, []);

    const setBulkPrefetchedCourseData = useCallback((dataByCourseId: Record<string, CachedCourseData>) => {
        const entries = Object.entries(dataByCourseId);
        if (entries.length === 0) return;
        setPrefetchedCourseDataState((prev) => ({ ...prev, ...dataByCourseId }));
    }, []);

    const getPrefetchedCourse = useCallback((courseId: string) => {
        return prefetchedCourseData[courseId] ?? null;
    }, [prefetchedCourseData]);

    const setCourseListPreview = useCallback((courseId: string, data: CourseCard | null) => {
        setCourseListPreviewByIdState((prev) => {
            if (!data) {
                if (!(courseId in prev)) return prev;
                const { [courseId]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [courseId]: data };
        });
    }, []);

    const getCourseListPreview = useCallback(
        (courseId: string) => courseListPreviewById[courseId] ?? null,
        [courseListPreviewById],
    );

    const getCourseIdForLesson = useCallback(
        (lessonId: string) => lessonToCourseId[lessonId] ?? null,
        [lessonToCourseId],
    );

    const setCoursesListCache = useCallback((cacheKey: string, courses: CourseCard[] | null) => {
        setCoursesListByKeyState((prev) => {
            if (!courses) {
                if (!(cacheKey in prev)) return prev;
                const { [cacheKey]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [cacheKey]: courses };
        });
    }, []);

    const getCoursesListCache = useCallback(
        (cacheKey: string) => coursesListByKey[cacheKey] ?? null,
        [coursesListByKey],
    );

    const clearCourseNavigationCache = useCallback(
        (courseId: string, options?: { clearStreak?: boolean }) => {
            setPrefetchedCourseData(courseId, null);
            setCourseListPreview(courseId, null);
            if (options?.clearStreak) {
                setPrefetchedStreak(null);
            }
        },
        [setPrefetchedCourseData, setCourseListPreview],
    );

    const value = useMemo(() => ({
        prefetchedCourseData,
        setPrefetchedCourseData,
        setBulkPrefetchedCourseData,
        getPrefetchedCourse,
        prefetchedStreak,
        setPrefetchedStreak,
        clearCourseNavigationCache,
        setCourseListPreview,
        getCourseListPreview,
        getCourseIdForLesson,
        setCoursesListCache,
        getCoursesListCache,
    }), [
        prefetchedCourseData,
        setPrefetchedCourseData,
        setBulkPrefetchedCourseData,
        getPrefetchedCourse,
        prefetchedStreak,
        setPrefetchedStreak,
        clearCourseNavigationCache,
        setCourseListPreview,
        getCourseListPreview,
        getCourseIdForLesson,
        setCoursesListCache,
        getCoursesListCache,
    ]);

    return (
        <CourseNavigationContext.Provider value={value}>
            {children}
        </CourseNavigationContext.Provider>
    );
}

export function useCourseNavigation() {
    const ctx = useContext(CourseNavigationContext);
    return ctx;
}
